/**
 * Determines if the given value is `undefined`.
 *
 * @template A - The type of the value being checked.
 * @param a - The value to check.
 * @returns `true` if the value is `undefined`, otherwise `false`.
 */
export const isUndefined = <A>(a: A | undefined): a is undefined =>
  a === undefined

/**
 * Determines if the given value is not `undefined`.
 *
 * @template A - The type of the value being checked.
 * @param a - The value to check.
 * @returns `true` if the value is not `undefined`, otherwise `false`.
 */
export const isNotUndefined = <A>(a: A | undefined): a is A => a !== undefined

/**
 * Throws a `TypeError` if the given value is `undefined`.
 *
 * @template A - The type of the value being checked.
 * @param a - The value to check.
 * @throws {TypeError} If the value is `undefined`.
 */
export function assertIsUndefined<A>(a: A | undefined): asserts a is undefined {
  if (isUndefined(a)) {
    return
  }

  throw new TypeError('Expected undefined')
}

/**
 * Throws a `TypeError` if the given value is not `undefined`.
 *
 * @template A - The type of the value being checked.
 * @param a - The value to check.
 * @throws {TypeError} If the value is not `undefined`.
 */
export function assertIsNotUndefined<A>(a: A | undefined): asserts a is A {
  if (isNotUndefined(a)) {
    return
  }

  throw new TypeError('Expected not undefined')
}
